import React, { useEffect, useMemo, useState } from "react";

import { fetchRedemptionPreview, requestRedemption } from "../../services/investmentService";
import { formatRedemptionApiError } from "../../utils/redemptionApiErrors";
import {
  availableSharesFromPreview,
  formatShareQty,
  maxSharesPerRequest,
  validateRedemptionSharesInput,
} from "../../utils/investorRedeemEligibility";

const bdt = new Intl.NumberFormat("en-BD", { style: "currency", currency: "BDT", minimumFractionDigits: 2 });

/**
 * Shared redemption request form (Redeemable Projects modal + funded project redeem tab).
 */
export default function InvestorRedeemSharesForm({
  projectId,
  available: availableProp,
  title = "Redeem shares",
  subtitle = "Request a sale to treasury; admin approves and fees apply at settlement.",
  submitLabel = "Submit redeem request",
  onSuccess,
  footerExtra = null,
  className = "stack invx-trade-card invx-trade-list",
}) {
  const projectIdStr = String(projectId || "").trim();
  const [shares, setShares] = useState("");
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");

  const available = useMemo(() => {
    if (availableProp != null && Number(availableProp) >= 0) return Number(availableProp);
    return availableSharesFromPreview(preview);
  }, [availableProp, preview]);

  const requestCap = useMemo(() => maxSharesPerRequest(preview, available), [preview, available]);

  const validation = useMemo(
    () => validateRedemptionSharesInput(shares, available, preview),
    [shares, available, preview]
  );

  const displayError = apiError || (!validation.valid && shares !== "" ? validation.message : "");

  useEffect(() => {
    setPreview(null);
    setApiError("");
    setShares("");
  }, [projectIdStr]);

  useEffect(() => {
    if (!projectIdStr || available <= 0) return undefined;
    let cancelled = false;
    const seed = String(Math.min(available, 1));
    fetchRedemptionPreview(projectIdStr, seed)
      .then(({ data }) => {
        if (cancelled) return;
        const cap = maxSharesPerRequest(data, available);
        setShares(cap > 0 ? String(cap) : "");
        setPreview(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setShares("");
          setApiError(formatRedemptionApiError(err));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [projectIdStr, available]);

  useEffect(() => {
    if (!projectIdStr) return undefined;
    const sh = Number(shares);
    if (!Number.isFinite(sh) || sh <= 0) {
      return undefined;
    }
    const local = validateRedemptionSharesInput(shares, available, preview);
    if (!local.valid) {
      return undefined;
    }
    let cancelled = false;
    const t = window.setTimeout(() => {
      fetchRedemptionPreview(projectIdStr, String(shares))
        .then(({ data }) => {
          if (!cancelled) {
            setPreview(data);
            setApiError("");
          }
        })
        .catch((err) => {
          if (!cancelled) {
            setPreview(null);
            setApiError(formatRedemptionApiError(err));
          }
        });
    }, 320);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [projectIdStr, shares, available]);

  const canSubmit =
    projectIdStr &&
    validation.valid &&
    preview &&
    !submitting &&
    available > 0;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    const check = validateRedemptionSharesInput(shares, available, preview);
    if (!check.valid) return;
    setApiError("");
    setSubmitting(true);
    try {
      const { data } = await requestRedemption({
        project_id: projectIdStr,
        shares: Number(shares),
      });
      onSuccess?.(data);
    } catch (err) {
      setApiError(formatRedemptionApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const maxPct = preview?.max_redemption_pct_of_holdings;

  return (
    <form className={className} onSubmit={onSubmit}>
      {title ? <h3 className="invx-trade-title">{title}</h3> : null}
      {subtitle ? <p className="muted invx-trade-sub">{subtitle}</p> : null}
      {available > 0 ? (
        <p className="muted" style={{ fontSize: 13, margin: "0 0 8px" }}>
          Available: <strong>{formatShareQty(available)}</strong> shares
          {requestCap > 0 && requestCap < available ? (
            <>
              {" "}
              · Max per request: <strong>{formatShareQty(requestCap)}</strong>
              {maxPct != null && maxPct !== "" ? ` (${maxPct}% of holding)` : ""}
            </>
          ) : null}
        </p>
      ) : null}
      <input
        placeholder="Shares to redeem"
        type="number"
        min="0.01"
        step="0.01"
        max={requestCap > 0 ? requestCap : available > 0 ? available : undefined}
        value={shares}
        onChange={(e) => {
          setApiError("");
          setShares(e.target.value);
        }}
        required
      />
      {preview && validation.valid ? (
        <div className="muted invx-transfer-preview redeem-shares-form__preview" style={{ fontSize: 13, lineHeight: 1.5 }}>
          <div>
            <strong>NAV / share:</strong> {bdt.format(Number(preview.nav_per_share || 0))}
          </div>
          <div>
            <strong>Gross:</strong> {bdt.format(Number(preview.gross_bdt || 0))} ·{" "}
            <strong>Redemption fee:</strong> {bdt.format(Number(preview.fee_total_bdt || 0))}
          </div>
          <div>
            <strong>Est. net cash:</strong> {bdt.format(Number(preview.net_bdt || 0))}
          </div>
          {preview.max_shares_per_request != null ? (
            <div>
              <strong>Max per request (platform):</strong> {preview.max_shares_per_request} sh (of {preview.shares_held}{" "}
              held)
            </div>
          ) : null}
        </div>
      ) : null}
      {displayError ? (
        <p className="msg msg-error" role="alert">
          {displayError}
        </p>
      ) : null}
      {submitting ? (
        <p className="msg" role="status">
          Submitting request…
        </p>
      ) : null}
      <button type="submit" className="invx-trade-submit" disabled={!canSubmit} aria-busy={submitting}>
        {submitting ? "Submitting…" : submitLabel}
      </button>
      {footerExtra}
    </form>
  );
}
